interface BusinessChallengesSectionProps {
  isVisible: boolean;
  sectionRef: React.RefObject<HTMLElement | null>;
  name?: string;
  description?: string;
  background?: string;
  nameColor?: string;
  descriptionColor?: string;
  items?: any[]; // Nhận items từ props thay vì tự call API
}

interface ChallengeItem {
  id: string;
  icon: string;
  title: string;
  description: string;
  iconBg?: string;
  color?: string;
}

export default function BusinessChallengesSection({
  isVisible,
  sectionRef,
  name = 'Doanh nghiệp thường gặp gì khi làm công nghệ?',
  description = 'Những rào cản ngăn bước tiến của doanh nghiệp trong kỷ nguyên số.',
  background,
  nameColor,
  descriptionColor,
  items = [] // Nhận từ props
}: BusinessChallengesSectionProps) {
  const challenges: ChallengeItem[] = items || [];
  const loading = false; // Không cần loading vì đã có data từ props
  const backgroundStyle = background
    ? {
        backgroundImage: background.startsWith('http') || background.startsWith('/')
          ? `url(${background})`
          : undefined,
        backgroundColor: background.startsWith('http') || background.startsWith('/') ? undefined : background,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : undefined;

  return (
    <section 
      ref={sectionRef}
      data-section="businessChallenges"
      className={`py-20 md:py-28 lg:py-32 relative overflow-hidden ${!background ? 'bg-gradient-to-b from-white via-gray-50/30 to-white' : ''}`}
      style={backgroundStyle}
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
        <div className="absolute top-20 left-10 w-96 h-96 bg-[#2a4b7c] rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-[500px] h-[500px] bg-[#00c897] rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className={`text-center mb-12 md:mb-16 lg:mb-20 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <h2 
            className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 md:mb-5 leading-[1.2] tracking-[-0.02em]"
            style={{ color: nameColor || '#2a4b7c' }}
          >
            {name}
          </h2>
          {description && (
            <p 
              className="text-base md:text-lg max-w-2xl mx-auto leading-[1.7] font-normal"
              style={{ color: descriptionColor || '#64748b' }}
            >
              {description}
            </p>
          )}
        </div>

        {/* Challenges Grid - 4 Columns */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block w-16 h-16 border-4 border-[#2a4b7c] border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-gray-600">Đang tải...</p>
          </div>
        ) : challenges.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600">Chưa có nội dung</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {challenges.map((challenge, index) => (
              <div
                key={challenge.id}
              className={`group relative bg-white rounded-xl md:rounded-2xl p-6 md:p-7 lg:p-8 border border-gray-200 hover:border-[#2a4b7c]/50 transition-all duration-500 hover:shadow-xl hover:-translate-y-2 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              {/* Background gradient on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#2a4b7c]/0 via-[#00c897]/0 to-[#00b587]/0 group-hover:from-[#2a4b7c]/5 group-hover:via-[#00c897]/5 group-hover:to-[#00b587]/5 transition-all duration-500 rounded-xl md:rounded-2xl"></div>
              
              {/* Content */}
              <div className="relative">
                {/* Icon */}
                <div className={`w-14 h-14 md:w-16 md:h-16 lg:w-20 lg:h-20 flex items-center justify-center ${challenge.iconBg || 'bg-gradient-to-br from-[#2a4b7c] to-[#1e3a5f]'} rounded-lg md:rounded-xl mb-5 md:mb-6 group-hover:from-[#00c897] group-hover:to-[#00b587] group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-md group-hover:shadow-[#00c897]/40`}>
                  <i className={`${challenge.icon} text-xl md:text-2xl lg:text-3xl ${challenge.color || 'text-white'}`}></i>
                </div>

                {/* Text Content */}
                <h3 className="text-base md:text-lg lg:text-xl font-bold text-[#2a4b7c] mb-3 md:mb-4 group-hover:text-[#00c897] transition-colors duration-300 leading-tight">
                  {challenge.title}
                </h3>
                <p className="text-sm md:text-base text-[#64748b] leading-relaxed">
                  {challenge.description}
                </p>

                {/* Decorative accent line */}
                <div className="mt-6 md:mt-8 h-1 w-0 group-hover:w-full bg-[#06b6d4] transition-all duration-700 rounded-full"></div>
              </div>
            </div>
          ))}
        </div>
        )}
      </div>
    </section>
  );
}
